import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { AnimatePresence, motion } from "framer-motion"
import { FileQuestion, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react"
import { useRef, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import {
	type AdminPost,
	type AdminPostsParams,
	deleteAdminPost,
	getAdminPosts,
	PLATFORM_LABEL,
	PLATFORMS,
	PlacementPill,
	type Platform,
	PostCover,
	placementOf,
	restoreAdminPost,
} from "@/entities/admin-post"
import { useSessionRole } from "@/entities/session"
import { PostsSearchBar } from "@/features/search-posts"
import { formatShortDate } from "@/shared/lib/format-date"
import { fadeUp, staggerContainer, TRANSITION } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"
import { AccessDenied } from "@/shared/ui/access-denied"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { EmptyState } from "@/shared/ui/empty-state"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { PageShell } from "@/shared/ui/page-shell"
import { RowActionsMenu } from "@/shared/ui/row-actions-menu"
import { LoadSwap } from "@/shared/ui/load-swap"
import { Skeleton } from "@/shared/ui/skeleton"
import { SlidingPill } from "@/shared/ui/sliding-pill"
import { TabCount, TabText } from "@/shared/ui/tab-label"

const PAGE_SIZE = 25

type Status = NonNullable<AdminPostsParams["status"]>

const STATUSES: Array<{ value: Status; label: string }> = [
	{ value: "all", label: "Все" },
	{ value: "published", label: "Опубликованные" },
	{ value: "draft", label: "Черновики" },
	{ value: "deleted", label: "Удалённые" },
]

const WHERE: Record<Platform, string> = {
	telegram: "в канале",
	tma: "в мини-аппе",
	mobile: "в мобильном",
}

function useStatusCounts(enabled: boolean) {
	return useQuery({
		queryKey: ["admin-posts", "counts"],
		enabled,
		queryFn: async () => {
			const [all, published, draft, deleted] = await Promise.all(
				STATUSES.map((status) => getAdminPosts({ status: status.value, limit: 1 })),
			)
			return {
				all: all!.total,
				published: published!.total,
				draft: draft!.total,
				deleted: deleted!.total,
			} satisfies Record<Status, number>
		},
	})
}

function PostRowActions({ post, onChanged }: { post: AdminPost; onChanged: () => void }) {
	const { confirm } = useConfirm()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const title = post.title || "Без названия"
	const refresh = async () => {
		await queryClient.invalidateQueries({ queryKey: ["admin-posts"] })
		onChanged()
	}
	const remove = useMutation({
		mutationFn: () => deleteAdminPost(post.id),
		onSuccess: async () => {
			toast.success("Материал удалён")
			await refresh()
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось удалить")),
	})
	const restore = useMutation({
		mutationFn: () => restoreAdminPost(post.id),
		onSuccess: async () => {
			toast.success("Материал восстановлен")
			await refresh()
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось восстановить")),
	})
	return (
		<RowActionsMenu
			label={`Действия: ${title}`}
			actions={
				post.deletedAt
					? [{ label: "Восстановить", icon: RotateCcw, onSelect: () => restore.mutate() }]
					: [
							{
								label: "Редактировать",
								icon: Pencil,
								onSelect: () => navigate(`/admin/edit/${post.id}`),
							},
							{
								label: "Удалить",
								icon: Trash2,
								destructive: true,
								separatorBefore: true,
								onSelect: () =>
									confirm({
										title: `Удалить «${title}»?`,
										message:
											"Материал пропадёт из приложений, а сообщение удалится из канала. Вернуть можно из раздела «Удалённые».",
										confirmText: "Удалить",
										onConfirm: () => remove.mutateAsync().then(() => undefined),
									}),
							},
						]
			}
		/>
	)
}

function Formats({ post }: { post: AdminPost }) {
	if (!post.formats?.length) return null
	return (
		<span className="truncate text-xs text-muted-foreground">
			{post.formats.map((format) => format.name).join(" · ")}
		</span>
	)
}

/** Заглушка по форме списка: на телефоне карточки, на компьютере таблица с теми же колонками. */
function PostsSkeleton({ rows }: { rows: number }) {
	const items = Array.from({ length: rows }, (_, index) => index)
	return (
		<div aria-hidden="true">
			<ul className="space-y-2 lg:hidden">
				{items.map((index) => (
					<li
						key={index}
						className="flex items-center gap-3 rounded-2xl border border-border bg-card p-2.5"
					>
						<Skeleton className="aspect-video w-24 shrink-0 rounded-lg" />
						<div className="min-w-0 flex-1 space-y-2">
							<Skeleton className="h-4 w-3/4" />
							<Skeleton className="h-3 w-1/2" />
						</div>
					</li>
				))}
			</ul>
			<div className="hidden overflow-hidden rounded-2xl border border-border bg-card lg:block">
				<table className="w-full text-left text-sm">
					<thead className="border-b border-border text-xs text-muted-foreground">
						<tr>
							<th className="py-3 pr-3 pl-4 font-medium">Материал</th>
							{PLATFORMS.map((item) => (
								<th key={item} className="w-36 px-3 py-3 font-medium">
									{PLATFORM_LABEL[item]}
								</th>
							))}
							<th className="w-24 px-3 py-3 font-medium">Изменён</th>
							<th className="w-14 py-3 pr-3" />
						</tr>
					</thead>
					<tbody>
						{items.map((index) => (
							<tr key={index} className="border-b border-border last:border-0">
								<td className="py-2.5 pr-3 pl-4">
									<div className="flex items-center gap-3">
										<Skeleton className="aspect-video w-20 shrink-0 rounded-lg" />
										<div className="min-w-0 flex-1 space-y-1.5">
											<Skeleton className="h-4" style={{ width: `${70 - ((index * 13) % 35)}%` }} />
											<Skeleton className="h-3 w-1/4" />
										</div>
									</div>
								</td>
								{PLATFORMS.map((item) => (
									<td key={item} className="px-3 py-2.5">
										<Skeleton className="h-4 w-24" />
									</td>
								))}
								<td className="px-3 py-2.5">
									<Skeleton className="h-4 w-12" />
								</td>
								<td className="py-2.5 pr-3">
									<Skeleton className="size-8 rounded-full" />
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	)
}

export function AdminPage() {
	const { isAdmin } = useSessionRole()
	const navigate = useNavigate()
	const [params, setParams] = useSearchParams()
	const status = STATUSES.find((item) => item.value === params.get("status"))?.value ?? "all"
	const platform = PLATFORMS.find((item) => item === params.get("platform"))
	const page = Math.max(1, Number(params.get("page")) || 1)
	const search = params.get("q") ?? ""
	const [input, setInput] = useState(search)
	const withPlatform = status === "published" || status === "draft"
	const options: AdminPostsParams = {
		status,
		platform: withPlatform ? platform : undefined,
		page,
		limit: PAGE_SIZE,
		search,
	}
	const query = useQuery({
		queryKey: ["admin-posts", options],
		queryFn: () => getAdminPosts(options),
		enabled: isAdmin,
		placeholderData: (previous) => previous,
	})
	const counts = useStatusCounts(isAdmin)
	const queryClient = useQueryClient()

	// Анимацию вешаем на ключ данных, которые реально на экране. Пока идёт запрос, на экране
	// прежний список (placeholderData): ключ от фильтра заставил бы его погаснуть и
	// проявиться заново ещё до прихода новых данных.
	const currentKey = `${options.status}-${options.platform}-${page}-${search}`
	const shownKey = useRef(currentKey)
	if (!query.isPlaceholderData) shownKey.current = currentKey
	// Каскад по строкам только на первом показе. Дальше смену данных ведёт перекрёстный
	// переход всего блока ниже, а каскад поверх него растянул бы смену почти на секунду.
	// После первой смены фильтра он не включается, в том числе при возврате на первый таб.
	const introKey = useRef<string | null>(null)
	const introDone = useRef(false)
	if (query.data && introKey.current === null) introKey.current = shownKey.current
	if (introKey.current !== null && shownKey.current !== introKey.current) introDone.current = true
	const intro = !introDone.current

	// Данные таба подтягиваем по наведению: к клику они уже в кэше.
	const prefetch = (value: Status) => {
		const next: AdminPostsParams = { status: value, page: 1, limit: PAGE_SIZE, search }
		void queryClient.prefetchQuery({
			queryKey: ["admin-posts", next],
			queryFn: () => getAdminPosts(next),
			staleTime: 30_000,
		})
	}

	const change = (patch: Record<string, string>, replace = false) =>
		setParams(
			(previous) => {
				const next = new URLSearchParams(previous)
				for (const [key, value] of Object.entries(patch)) {
					if (value) next.set(key, value)
					else next.delete(key)
				}
				return next
			},
			{ replace },
		)

	if (!isAdmin) return <AccessDenied title="Контент" />
	const data = query.data
	const pages = data ? Math.ceil(data.total / PAGE_SIZE) : 0

	const statusLabel = (item: (typeof STATUSES)[number]) =>
		item.value === "draft" && withPlatform && platform ? "Не опубликованные" : item.label

	return (
		<PageShell
			eyebrow="Каталог"
			title="Контент"
			subtitle="Создайте материал, а потом опубликуйте его в канале, мини-аппе и мобильном приложении."
			action={
				<Button asChild className="rounded-full px-5">
					<Link to="/admin/create">
						<Plus />
						Новый материал
					</Link>
				</Button>
			}
		>
			<PostsSearchBar
				autoSearch
				value={input}
				onChange={setInput}
				onSearch={() => change({ q: input.trim(), page: "" }, true)}
			/>

			<div className="mt-4 mb-5 flex flex-wrap items-center gap-x-4 gap-y-3">
				<div
					role="tablist"
					aria-label="Статус"
					className="hide-scrollbar relative flex max-w-full gap-1 overflow-x-auto rounded-full bg-secondary/60 p-1"
				>
					<SlidingPill className="rounded-full" />
					{STATUSES.map((item) => {
						const active = status === item.value
						return (
							<button
								key={item.value}
								type="button"
								role="tab"
								aria-selected={active}
								data-active={active}
								onPointerEnter={() => prefetch(item.value)}
								onFocus={() => prefetch(item.value)}
								onClick={() => change({ status: item.value === "all" ? "" : item.value, page: "" })}
								className={cn(
									"relative shrink-0 rounded-full px-3.5 py-1.5 text-sm whitespace-nowrap transition-colors",
									active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
								)}
							>
								<span className="relative flex items-center">
									<TabText text={statusLabel(item)} />
									<TabCount value={counts.data?.[item.value]} />
								</span>
							</button>
						)
					})}
				</div>
				{/* Площадка прижата вправо: табы слева меняют ширину («Черновики» ⇄ «Не
				    опубликованные»), и кнопки не должны уезжать из-под курсора. */}
				<AnimatePresence initial={false}>
					{withPlatform ? (
						<motion.div
							key="platform"
							initial={{ opacity: 0, x: 6 }}
							animate={{ opacity: 1, x: 0, transition: TRANSITION.slow }}
							exit={{ opacity: 0, x: 6, transition: TRANSITION.fast }}
							className="flex flex-wrap items-center gap-1.5 text-sm lg:ml-auto"
							role="group"
							aria-label="Площадка"
						>
							<span className="text-muted-foreground">где:</span>
							{[undefined, ...PLATFORMS].map((item) => {
								const active = platform === item
								return (
									<button
										key={item ?? "any"}
										type="button"
										aria-pressed={active}
										onClick={() => change({ platform: item ?? "", page: "" })}
										className={cn(
											// Обводка сделана ring, то есть box-shadow: transition-colors его не ведёт.
											"rounded-full px-3 py-1 ring-1 transition-[color,background-color,box-shadow] duration-200 ease-[var(--ease-out-soft)]",
											active
												? "bg-primary/15 text-foreground ring-primary/50"
												: "text-muted-foreground ring-border hover:text-foreground",
										)}
									>
										{item ? (
											WHERE[item]
										) : (
											<TabText text={status === "draft" ? "нигде" : "где угодно"} />
										)}
									</button>
								)
							})}
						</motion.div>
					) : null}
				</AnimatePresence>
			</div>

			<LoadSwap loading={query.isLoading}>
				{/* Любая смена показанных данных (таб, площадка, страница, поиск, «пусто») идёт
				    перекрёстным переходом: старый блок гаснет поверх, не занимая места, новый
				    проявляется на его месте. Мгновенная подмена читается резко, а затухание
				    всего списка в ноль и обратно мельканием, поэтому они наплывают друг на друга.
				    Ключ меняется, только когда пришли новые данные: пока идёт запрос, на экране
				    прежний список. Загрузка и первый список делят ключ, их переход ведёт LoadSwap. */}
				<AnimatePresence initial={false} mode="popLayout">
					<motion.div
						key={
							query.isError
								? "error"
								: query.isLoading || data?.posts.length
									? `list-${shownKey.current}`
									: "empty"
						}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } }}
						// Уходящий блок лежит поверх нового: клики должны попадать в новый.
						exit={{
							opacity: 0,
							pointerEvents: "none",
							transition: { duration: 0.24, ease: [0.4, 0, 0.2, 1] },
						}}
					>
						{query.isLoading ? (
							<PostsSkeleton rows={8} />
						) : query.isError ? (
							<LoadErrorState onRetry={() => void query.refetch()} />
						) : data?.posts.length ? (
							<>
								<div
									className={cn(
										"transition-opacity",
										// Подтемнение только если ответа ждём дольше 200 мс: на быстром ответе
										// список не мигает, а возвращается к полной яркости сразу.
										query.isFetching ? "opacity-70 delay-200 duration-200" : "duration-150",
									)}
								>
									{/* Телефон: карточки. */}
									<motion.ul
										variants={intro ? staggerContainer(0.025) : undefined}
										initial={intro ? "hidden" : false}
										animate={intro ? "visible" : undefined}
										className="space-y-2 lg:hidden"
									>
										{data.posts.map((post) => (
											<motion.li
												key={post.id}
												variants={intro ? fadeUp : undefined}
												className={cn(
													"flex items-center gap-3 rounded-2xl border border-border bg-card p-2.5",
													post.deletedAt && "opacity-60",
												)}
											>
												<Link
													to={`/admin/posts/${post.id}`}
													className="flex min-w-0 flex-1 items-center gap-3"
												>
													<PostCover media={post.media} className="w-24" />
													<div className="min-w-0 flex-1 space-y-1">
														<p className="line-clamp-2 text-sm font-medium">
															{post.title || "Без названия"}
														</p>
														<div className="flex flex-wrap gap-x-3 gap-y-1">
															{PLATFORMS.map((item) => (
																<PlacementPill
																	key={item}
																	state={placementOf(post, item).state}
																	label={PLATFORM_LABEL[item]}
																	className="text-xs"
																/>
															))}
														</div>
													</div>
												</Link>
												<PostRowActions post={post} onChanged={() => void counts.refetch()} />
											</motion.li>
										))}
									</motion.ul>

									{/* Компьютер: таблица. */}
									<div className="hidden overflow-hidden rounded-2xl border border-border bg-card lg:block">
										<table className="w-full text-left text-sm">
											<thead className="border-b border-border text-xs text-muted-foreground">
												<tr>
													<th className="py-3 pr-3 pl-4 font-medium">Материал</th>
													{PLATFORMS.map((item) => (
														<th key={item} className="w-36 px-3 py-3 font-medium">
															{PLATFORM_LABEL[item]}
														</th>
													))}
													<th className="w-24 px-3 py-3 font-medium">Изменён</th>
													<th className="w-14 py-3 pr-3">
														<span className="sr-only">Действия</span>
													</th>
												</tr>
											</thead>
											<motion.tbody
												variants={intro ? staggerContainer(0.02) : undefined}
												initial={intro ? "hidden" : false}
												animate={intro ? "visible" : undefined}
											>
												{data.posts.map((post) => (
													<motion.tr
														key={post.id}
														variants={intro ? fadeUp : undefined}
														onClick={(event) => {
															if ((event.target as HTMLElement).closest("a,button")) return
															navigate(`/admin/posts/${post.id}`)
														}}
														className={cn(
															"group cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-accent/40",
															post.deletedAt && "opacity-60",
														)}
													>
														<td className="py-2.5 pr-3 pl-4">
															<div className="flex items-center gap-3">
																<PostCover media={post.media} className="w-20" />
																<div className="flex min-w-0 flex-col gap-0.5">
																	<Link
																		to={`/admin/posts/${post.id}`}
																		className="link-underline line-clamp-1 self-start font-medium group-hover:[--link-underline:100%] focus-visible:[--link-underline:100%]"
																	>
																		{post.title || "Без названия"}
																	</Link>
																	<Formats post={post} />
																</div>
															</div>
														</td>
														{PLATFORMS.map((item) => (
															<td key={item} className="px-3 py-2.5">
																<PlacementPill state={placementOf(post, item).state} />
															</td>
														))}
														<td className="px-3 py-2.5 text-muted-foreground tabular-nums">
															{formatShortDate(post.updatedAt)}
														</td>
														<td className="py-2.5 pr-3">
															<PostRowActions post={post} onChanged={() => void counts.refetch()} />
														</td>
													</motion.tr>
												))}
											</motion.tbody>
										</table>
									</div>
								</div>
								{/* Пагинация часть блока списка: при уходе в пустой фильтр гаснет вместе с ним на
				    своём месте, а не подпрыгивает к заглушке. */}
								{pages > 1 ? (
									<nav
										aria-label="Страницы"
										className="mt-6 flex items-center justify-between gap-3"
									>
										<Button
											variant="secondary"
											disabled={page <= 1}
											onClick={() => change({ page: String(page - 1) })}
										>
											Назад
										</Button>
										<span className="text-sm text-muted-foreground tabular-nums">
											Страница {page} из {pages} · {data.total} материалов
										</span>
										<Button
											variant="secondary"
											disabled={page >= pages}
											onClick={() => change({ page: String(page + 1) })}
										>
											Далее
										</Button>
									</nav>
								) : null}
							</>
						) : (
							<EmptyState
								icon={FileQuestion}
								title={search ? "Ничего не нашлось" : "Здесь пока пусто"}
								action={
									status !== "all" || search ? (
										<Button
											variant="secondary"
											className="rounded-full px-5"
											onClick={() => {
												setInput("")
												setParams({})
											}}
										>
											Показать все материалы
										</Button>
									) : (
										<Button asChild className="rounded-full px-5">
											<Link to="/admin/create">
												<Plus />
												Новый материал
											</Link>
										</Button>
									)
								}
							>
								{search
									? `По запросу «${search}» материалов нет.`
									: status === "draft" && platform
										? `Всё уже опубликовано ${WHERE[platform]}.`
										: "Под этот фильтр материалы не попали."}
							</EmptyState>
						)}
					</motion.div>
				</AnimatePresence>
			</LoadSwap>
		</PageShell>
	)
}
